<template>
  <div>
    <n-loading-spinner v-if="isLoading" />
    <div v-if="!isLoading">
      <div v-for="intent in intentsForFlows" :key="intent.id" class="intent-list">
        <a @click="chooseIntent(intent)" class="dropdown-item">
          {{ intent.title }}
        </a>
      </div>
      <hr class="dropdown-divider" v-if="intentsForFlows.length > 0" />
      <a class="dropdown-item">
        <div class="add-item">
          <a class="add-intent" @click="addNewIntent">
            <AddIcon class="icon-1-3x"></AddIcon>
            Add new intent
          </a>
        </div>
      </a>
    </div>

    <AddIntentModal
      v-if="showAddIntentModel"
      ref="intentConditionAddModal"
      @completed="addedIntent"
      @cancelled="cancelled"
    />
  </div>
</template>

<script lang="ts" setup>
import { IntentListItemViewModel } from '@/features/bots/models/intents/intentListItemViewModel';
import AddIntentModal from '@/features/bots/components/designer/AddIntentModal.vue';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import { IntentViewModel } from '@/features/bots/models/intents/intentViewModel';
import { required } from '@vuelidate/validators';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { LOAD_INTENTS } from '@/store/intents/actionTypes';
import Toast from '@/shared/services/toast';
import { computed, nextTick, onMounted, PropType, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { useStore } from 'vuex';
import { TriggerConditionAttributeType } from '@/features/bots/components/designer/Conditions/useTriggerConditions';

const props = defineProps({
  condition: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'changed', value: ConditionViewModel): void;
  (e: 'completed', value: ConditionViewModel): void;
}>();

const store = useStore();
const intents = computed<IntentListItemViewModel[]>(() => store.getters.intents);
const loadIntents = (forceLoad: boolean = false): Promise<IntentListItemViewModel[]> =>
  store.dispatch(LOAD_INTENTS, forceLoad);

const selectedIntentId = ref('');
const isLoading = ref(false);
const liveCondition = ref<ConditionViewModel>({
  attribute: TriggerConditionAttributeType.MessageIntent,
  operation: '',
  type: '',
  value: '',
  result: true,
});
const showAddIntentModel = ref(false);
const intentConditionAddModal = ref<typeof AddIntentModal | null>(null);

const intentsForFlows = computed(() => intents.value.filter((e) => e.id !== 'None'));

const validations = {
  liveCondition: {
    value: { required },
  },
};

const $v = useVuelidate(validations, { liveCondition });

onMounted(async () => {
  isLoading.value = true;
  try {
    await loadIntents();
  } catch {
    Toast.showError('Failed to load intents');
  }
  isLoading.value = false;

  if (props.condition) {
    liveCondition.value = {
      attribute: TriggerConditionAttributeType.MessageIntent,
      operation: 'equals',
      type: props.condition.type,
      value: props.condition.value,
      result: props.condition.result,
      unit: props.condition.unit,
      invalid: props.condition.invalid,
    };

    if (liveCondition.value.invalid) {
      $v.value.$touch();
    }
  }

  emitConditionChanged();
});

async function chooseIntent(intent: IntentListItemViewModel) {
  selectedIntentId.value = intent.id;
  chosenIntent();
}

function addNewIntent() {
  showAddIntentModel.value = true;
  nextTick(() => {
    if (intentConditionAddModal.value) {
      intentConditionAddModal.value.addNewIntent();
    }
  });
}

function addedIntent(intent: IntentViewModel) {
  selectedIntentId.value = intent.id;
  showAddIntentModel.value = false;
  chosenIntent();
}

function cancelled() {
  showAddIntentModel.value = false;
  complete();
}

function chosenIntent() {
  liveCondition.value.value = selectedIntentId.value;
  complete();
}

function emitConditionChanged() {
  liveCondition.value.invalid = $v.value.$invalid;
  emit('changed', liveCondition.value);
}

function complete() {
  liveCondition.value.invalid = $v.value.$invalid;
  emit('completed', liveCondition.value);
}
</script>

<style scoped lang="scss">
.intent-list {
  display: flex;
  .edit-icon {
    color: grey;
    position: absolute;
    right: 0;

    &:hover {
      color: black;
    }
  }
}

.add-item {
  display: flex;

  .icon-1-3x {
    position: relative;
    top: 2px;
    color: $color-netcall-deep-sea;
  }

  .add-intent {
    color: $color-netcall-deep-sea;
  }
}

.dropdown-divider {
  background-color: #ededed;
  border: none;
  display: block;
  height: 1px;
  margin: 0.5rem 0;
}

:deep(.sk-folding-cube) {
  margin: 2rem auto;
}
</style>
